// Money value object — ADR-0014 / INV-03.
// Amounts are decimals with 4 fractional digits internally (matches NUMERIC(19,4)).
// Rounding is "half away from zero" (02b §0.2). No `number` arithmetic anywhere here.
import { Decimal } from 'decimal.js';
/** ponytail: static registry for now; becomes DB-backed (`pms.currency`) in P2 */
const DEFAULT_CURRENCIES = {
    SYP: { code: 'SYP', minorUnits: 0 }, // [Assumption] OD-24
    XSO: { code: 'XSO', minorUnits: 0 }, // legacy SYP (100 XSO = 1 SYP), imports only
    USD: { code: 'USD', minorUnits: 2 },
    EUR: { code: 'EUR', minorUnits: 2 },
    TRY: { code: 'TRY', minorUnits: 2 },
    SAR: { code: 'SAR', minorUnits: 2 },
    AED: { code: 'AED', minorUnits: 2 },
    JOD: { code: 'JOD', minorUnits: 3 },
};
export const SCALE = 4;
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export class CurrencyMismatchError extends Error {
    constructor(a, b) {
        super(`Currency mismatch: ${a} vs ${b}`);
        this.name = 'CurrencyMismatchError';
    }
}
export class InvalidMoneyError extends Error {
    constructor(msg) {
        super(msg);
        this.name = 'InvalidMoneyError';
    }
}
function round4(d) {
    return d.toDecimalPlaces(SCALE, Decimal.ROUND_HALF_UP);
}
export class Money {
    value;
    currency;
    constructor(value, // always already rounded to SCALE
    currency) {
        this.value = value;
        this.currency = currency;
    }
    /** Parse from a string such as "80.0000" or "-7.5". Never from a float. */
    static of(amount, currency) {
        if (typeof amount === 'string' && !/^-?\d+(\.\d+)?$/.test(amount.trim())) {
            throw new InvalidMoneyError(`Not a decimal string: "${amount}"`);
        }
        if (!/^[A-Z]{3}$/.test(currency))
            throw new InvalidMoneyError(`Bad currency code: ${currency}`);
        return new Money(round4(new D(amount)), currency);
    }
    static zero(currency) {
        return Money.of('0', currency);
    }
    /** From integer minor units, e.g. cents (used by UI inputs). */
    static fromMinor(minor, currency, info = currencyInfo(currency)) {
        if (typeof minor === 'number' && !Number.isSafeInteger(minor)) {
            throw new InvalidMoneyError('minor units must be an integer');
        }
        const d = new D(minor.toString()).div(new D(10).pow(info.minorUnits));
        return new Money(round4(d), currency);
    }
    /** Canonical 4-dp string, e.g. "80.0000" — what gets stored in NUMERIC(19,4). */
    toString() {
        return this.value.toFixed(SCALE);
    }
    /** Display/tender amount rounded to the currency's minor units (half away from zero). */
    toDisplayString(info = currencyInfo(this.currency)) {
        return this.value
            .toDecimalPlaces(info.minorUnits, Decimal.ROUND_HALF_UP)
            .toFixed(info.minorUnits);
    }
    /** Decimal copy (for callers that need further math; still never a float). */
    toDecimal() {
        return new D(this.value);
    }
    isZero() {
        return this.value.isZero();
    }
    isNegative() {
        return this.value.isNegative();
    }
    equals(other) {
        return this.currency === other.currency && this.value.eq(other.value);
    }
    compare(other) {
        this.assertSame(other);
        return this.value.cmp(other.value);
    }
    add(other) {
        this.assertSame(other);
        return new Money(round4(this.value.plus(other.value)), this.currency);
    }
    sub(other) {
        this.assertSame(other);
        return new Money(round4(this.value.minus(other.value)), this.currency);
    }
    negate() {
        return new Money(this.value.neg(), this.currency);
    }
    abs() {
        return new Money(this.value.abs(), this.currency);
    }
    /** Multiply by an exact decimal factor (quantity, percentage as fraction). Result rounded to SCALE. */
    times(factor) {
        return new Money(round4(this.value.times(new D(factor))), this.currency);
    }
    /** Divide by an exact decimal divisor. Result rounded to SCALE. */
    div(divisor) {
        const dv = new D(divisor);
        if (dv.isZero())
            throw new InvalidMoneyError('division by zero');
        return new Money(round4(this.value.div(dv)), this.currency);
    }
    /**
     * Convert using an exchange rate expressed as "1 unit of this.currency = rate units of `to`".
     * Rate precision is NUMERIC(24,10) in DB; passed as string.
     */
    convert(to, rate) {
        const r = new D(rate);
        if (!r.isPositive())
            throw new InvalidMoneyError('exchange rate must be > 0');
        return new Money(round4(this.value.times(r)), to);
    }
    /**
     * Split this amount into parts proportional to `weights` so that the parts sum EXACTLY to the total.
     * Rounding residue goes to the last part (02b BR-CHG-02 "absorb residue in last line").
     */
    allocate(weights) {
        if (weights.length === 0)
            throw new InvalidMoneyError('allocate needs at least one weight');
        const ws = weights.map((w) => new D(w));
        const total = ws.reduce((a, b) => a.plus(b), new D(0));
        if (total.isZero())
            throw new InvalidMoneyError('allocate weights sum to zero');
        const parts = [];
        let remaining = this.value;
        for (let i = 0; i < ws.length - 1; i++) {
            const part = round4(this.value.times(ws[i]).div(total));
            parts.push(new Money(part, this.currency));
            remaining = remaining.minus(part);
        }
        parts.push(new Money(round4(remaining), this.currency));
        return parts;
    }
    /**
     * Tax-inclusive breakdown: gross → { net, components[] } where each component is a rate (fraction).
     * Components are applied in the given order, each on the running base (net, then net+previous).
     * Matches the worked example in 02b §19.1: gross 80 with SC 10% and TAX 10% on (net+SC)
     * → net 66.1157, SC 6.6116, TAX 7.2727 (last absorbs residue).
     */
    splitInclusive(rates) {
        const rs = rates.map((r) => new D(r));
        const divisor = rs.reduce((acc, r) => acc.times(new D(1).plus(r)), new D(1));
        const net = round4(this.value.div(divisor));
        const components = [];
        let base = net;
        for (let i = 0; i < rs.length; i++) {
            if (i === rs.length - 1) {
                const sumSoFar = components.reduce((a, b) => a.plus(b), net);
                components.push(round4(this.value.minus(sumSoFar)));
            }
            else {
                const c = round4(base.times(rs[i]));
                components.push(c);
                base = base.plus(c);
            }
        }
        return {
            net: new Money(net, this.currency),
            components: components.map((c) => new Money(c, this.currency)),
        };
    }
    assertSame(other) {
        if (this.currency !== other.currency)
            throw new CurrencyMismatchError(this.currency, other.currency);
    }
}
export function currencyInfo(code) {
    const info = DEFAULT_CURRENCIES[code];
    if (!info)
        throw new InvalidMoneyError(`Unknown currency: ${code}`);
    return info;
}
/** Sum a list of Money (all same currency); empty list needs an explicit currency. */
export function sum(items, currency) {
    const first = items[0];
    if (!first) {
        if (!currency)
            throw new InvalidMoneyError('sum of empty list needs a currency');
        return Money.zero(currency);
    }
    return items.slice(1).reduce((acc, m) => acc.add(m), first);
}
//# sourceMappingURL=money.js.map